import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { inr, nc } from './IsaKpis';

const fmtDay = d => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const KIND = { scheduled: ['11:59 PM', 'bg-emerald-50 text-emerald-700'], manual: ['Manual', 'bg-blue-50 text-blue-700'], backfill: ['Back-filled', 'bg-amber-50 text-amber-700'] };

const Th = ({ children, right }) => <th className={`px-3 py-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold ${right ? 'text-right' : 'text-left'}`}>{children}</th>;

export const TeamTable = ({ data, onOpen }) => {
  const emps = data.employees.filter(e => data.people[e.key]);
  return (
    <Card className="overflow-x-auto" data-testid="isa-team-table">
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-bold text-slate-900">Combined team — where the money is calculated</h3>
        <p className="text-[11px] text-slate-500">Target, incentive and penalties use team totals. Net is split by each person's share % for that day.</p>
      </div>
      <table className="w-full text-xs min-w-[1000px]">
        <thead className="border-y border-slate-100 bg-slate-50/60">
          <tr><Th>Date</Th>{emps.map(e => <Th key={e.key} right>{e.name} Hot</Th>)}<Th right>Team Hot</Th><Th>Target</Th><Th right>Incentive</Th>
            <Th right>Team UTR</Th><Th right>Team Call Back</Th><Th right>Low target</Th><Th right>UTR pen.</Th><Th right>Call Back pen.</Th><Th right>Net</Th>{emps.map(e => <Th key={e.key} right>{e.name} share</Th>)}<Th>Snapshot</Th></tr>
        </thead>
        <tbody>
          {data.days.map(r => {
            const c = r.calc;
            const [kind, cls] = KIND[r.kind] || [r.kind, ''];
            return (
              <tr key={r.date} onClick={() => onOpen(r.date)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`isa-team-row-${r.date}`}>
                <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{fmtDay(r.date)}</td>
                {emps.map(e => <td key={e.key} className="px-3 py-2 text-right">{r.people[e.key]?.hot ?? '—'}</td>)}
                <td className="px-3 py-2 text-right font-bold">{c.team_hot}</td>
                <td className="px-3 py-2">{c.target_met ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Met</Badge> : <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Missed</Badge>}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{c.incentive ? inr(c.incentive) : '—'}</td>
                <td className="px-3 py-2 text-right">{nc(r.team.utr)}</td>
                <td className="px-3 py-2 text-right">{nc(r.team.callback)}</td>
                <td className="px-3 py-2 text-right text-rose-600">{c.low_target_penalty ? inr(-c.low_target_penalty) : '—'}</td>
                <td className="px-3 py-2 text-right text-rose-600">{c.utr_penalty ? inr(-c.utr_penalty) : '—'}</td>
                <td className="px-3 py-2 text-right text-rose-600">{c.callback_penalty ? inr(-c.callback_penalty) : '—'}</td>
                <td className={`px-3 py-2 text-right font-bold ${c.net < 0 ? 'text-rose-600' : 'text-slate-900'}`}>{inr(c.net)}</td>
                {emps.map(e => { const v = c.shares?.[e.key]; return <td key={e.key} className={`px-3 py-2 text-right ${v < 0 ? 'text-rose-600' : ''}`}>{v == null ? '—' : inr(v)}</td>; })}
                <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{kind}</span></td>
              </tr>
            );
          })}
          {data.days.length === 0 && <tr><td colSpan={10 + emps.length * 2} className="px-4 py-8 text-center text-slate-400">No snapshots saved for this period yet.</td></tr>}
        </tbody>
        {data.days.length > 0 && (
          <tfoot className="border-t-2 border-slate-200 font-bold text-slate-900">
            <tr data-testid="isa-team-total">
              <td className="px-3 py-2">Total</td>
              {emps.map(e => <td key={e.key} className="px-3 py-2 text-right">{data.people[e.key].hot}</td>)}
              <td className="px-3 py-2 text-right">{data.totals.team_hot}</td>
              <td className="px-3 py-2 text-[11px] font-normal">{data.totals.met_days} met</td>
              <td className="px-3 py-2 text-right text-emerald-700">{inr(data.totals.incentive)}</td>
              <td /><td />
              <td className="px-3 py-2 text-right text-rose-600">{inr(-data.totals.low_target_penalty)}</td>
              <td className="px-3 py-2 text-right text-rose-600">{inr(-data.totals.utr_penalty)}</td>
              <td className="px-3 py-2 text-right text-rose-600">{inr(-data.totals.callback_penalty)}</td>
              <td className={`px-3 py-2 text-right ${data.totals.net < 0 ? 'text-rose-600' : ''}`}>{inr(data.totals.net)}</td>
              {emps.map(e => <td key={e.key} className={`px-3 py-2 text-right ${data.people[e.key].share < 0 ? 'text-rose-600' : ''}`}>{inr(data.people[e.key].share)}</td>)}
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </Card>
  );
};

export const PersonTable = ({ data, emp, onOpen }) => {
  const p = data.people[emp.key];
  if (!p) return null;
  return (
    <Card className="overflow-x-auto" data-testid={`isa-person-table-${emp.key}`}>
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{emp.name} <span className="text-[11px] font-normal text-slate-400">{emp.emp_id}{emp.owner_name ? ` · CRM owner “${emp.owner_name}”` : ''}{emp.former ? ' · no longer listed' : ''}</span></h3>
          <p className="text-[11px] text-slate-500">Performance view only — no rupee rules apply per person.</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-slate-900" data-testid={`isa-person-hot-${emp.key}`}>{p.hot} Hot</div>
          <div className="text-[11px] text-slate-500">{p.contribution_pct}% of team · share {inr(p.share)}</div>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="border-y border-slate-100 bg-slate-50/60">
          <tr><Th>Date</Th><Th right>Hot leads</Th><Th right>Share of team Hot</Th><Th right>Unable to Reach</Th><Th right>Call Back</Th></tr>
        </thead>
        <tbody>
          {data.days.filter(r => r.people[emp.key]).map(r => {
            const x = r.people[emp.key];
            return (
              <tr key={r.date} onClick={() => onOpen(r.date)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                <td className="px-3 py-2 text-slate-800 whitespace-nowrap">{fmtDay(r.date)}</td>
                <td className="px-3 py-2 text-right font-semibold">{x.hot}</td>
                <td className="px-3 py-2 text-right text-slate-500">{r.calc.team_hot ? `${Math.round((x.hot * 1000) / r.calc.team_hot) / 10}%` : '—'}</td>
                <td className="px-3 py-2 text-right">{nc(x.utr)}</td>
                <td className="px-3 py-2 text-right">{nc(x.callback)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
};
